/**
 * EditorToolbar Component
 *
 * Main toolbar for the label layout editor with undo/redo, zoom, grid, and keyboard shortcuts.
 *
 * Features:
 * - Undo/redo buttons with history depth indicator
 * - Zoom in/out controls with percentage display
 * - Grid visibility and snap-to-grid toggles
 * - Reset button to restore default layout
 * - Keyboard shortcuts help modal
 * - Global keyboard event handling for all shortcuts
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Undo2,
  Redo2,
  Grid3X3,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  History,
} from 'lucide-react';
import { useLabelLayoutStore, useTemporalStore } from './label-layout-store';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { KeyboardShortcutsHelp } from './KeyboardShortcutsHelp';

// Nudge amounts in mm
const NUDGE_SMALL = 1;
const NUDGE_LARGE = 5;

export function EditorToolbar() {
  const { t } = useTranslation('settings');
  const scale = useLabelLayoutStore((s) => s.editor.scale);
  const showGrid = useLabelLayoutStore((s) => s.editor.showGrid);
  const snapToGrid = useLabelLayoutStore((s) => s.editor.snapToGrid);
  const selectedId = useLabelLayoutStore((s) => s.editor.selectedComponentId);
  const components = useLabelLayoutStore((s) => s.components);
  const setScale = useLabelLayoutStore((s) => s.setScale);
  const toggleGrid = useLabelLayoutStore((s) => s.toggleGrid);
  const toggleSnap = useLabelLayoutStore((s) => s.toggleSnap);
  const toggleVisibility = useLabelLayoutStore((s) => s.toggleVisibility);
  const updateComponent = useLabelLayoutStore((s) => s.updateComponent);
  const setSelectedComponent = useLabelLayoutStore((s) => s.setSelectedComponent);
  const reset = useLabelLayoutStore((s) => s.reset);

  // Live region announcement for screen readers
  const [announcement, setAnnouncement] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Ref for t() so keyboard handler can access translations without stale closures
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  // Helper to announce actions to screen readers
  const announce = useCallback((message: string) => {
    setAnnouncement(message);
    // Clear after announcement is read
    setTimeout(() => setAnnouncement(''), 1000);
  }, []);

  // Undo/redo from temporal store
  const { undo, redo, pastStates, futureStates } = useTemporalStore((state) => ({
    undo: state.undo,
    redo: state.redo,
    pastStates: state.pastStates,
    futureStates: state.futureStates,
  }));

  const canUndo = pastStates.length > 0;
  const canRedo = futureStates.length > 0;

  // ============================================================================
  // KEYBOARD HANDLER OPTIMIZATION
  // Using refs to avoid recreating the event listener on every state change.
  // The handler references mutable ref values, so it stays stable while
  // still accessing the latest state.
  // ============================================================================

  // Refs to hold mutable values accessed by the keyboard handler
  const stateRef = useRef({
    selectedId,
    components,
    canUndo,
    canRedo,
  });

  // Keep refs in sync with latest state
  useEffect(() => {
    stateRef.current = {
      selectedId,
      components,
      canUndo,
      canRedo,
    };
  }, [selectedId, components, canUndo, canRedo]);

  // Stable action refs (these functions are stable from Zustand, but we ref them for consistency)
  const actionsRef = useRef({
    undo,
    redo,
    updateComponent,
    setSelectedComponent,
    toggleVisibility,
    announce,
  });

  useEffect(() => {
    actionsRef.current = {
      undo,
      redo,
      updateComponent,
      setSelectedComponent,
      toggleVisibility,
      announce,
    };
  }, [undo, redo, updateComponent, setSelectedComponent, toggleVisibility, announce]);

  // Global keyboard shortcuts - stable handler that reads from refs
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if in input/textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      const { selectedId, components, canUndo, canRedo } = stateRef.current;
      const { undo, redo, updateComponent, setSelectedComponent, toggleVisibility, announce } = actionsRef.current;

      const isCtrl = e.ctrlKey || e.metaKey;

      // Undo/Redo
      if (isCtrl && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          if (canRedo) {
            redo();
            announce(tRef.current('production.editorToolbar.announced.redo'));
          }
        } else {
          if (canUndo) {
            undo();
            announce(tRef.current('production.editorToolbar.announced.undo'));
          }
        }
        return;
      }

      // Arrow keys for nudge (when component selected)
      if (selectedId && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const comp = components.find((c) => c.id === selectedId);
        if (!comp || comp.locked) return;

        const amount = e.shiftKey ? NUDGE_LARGE : NUDGE_SMALL;

        let dx = 0, dy = 0;
        let directionKey = '';
        switch (e.key) {
          case 'ArrowUp':
            dy = -amount;
            directionKey = 'movedUp';
            break;
          case 'ArrowDown':
            dy = amount;
            directionKey = 'movedDown';
            break;
          case 'ArrowLeft':
            dx = -amount;
            directionKey = 'movedLeft';
            break;
          case 'ArrowRight':
            dx = amount;
            directionKey = 'movedRight';
            break;
        }

        updateComponent(selectedId, {
          position: {
            x: comp.position.x + dx,
            y: comp.position.y + dy,
          },
        });
        announce(tRef.current(`production.editorToolbar.announced.${directionKey}`, { amount }));
        return;
      }

      // Escape to deselect - stop propagation to prevent modal close handler from also firing
      if (e.key === 'Escape' && selectedId) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedComponent(null);
        return;
      }

      // Delete/Backspace to hide (if not required)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        toggleVisibility(selectedId);
        return;
      }

      // L to toggle lock
      if (e.key === 'l' && selectedId && !isCtrl) {
        e.preventDefault();
        const comp = components.find((c) => c.id === selectedId);
        if (comp) updateComponent(selectedId, { locked: !comp.locked });
        return;
      }

      // V to toggle visibility
      if (e.key === 'v' && selectedId && !isCtrl) {
        e.preventDefault();
        toggleVisibility(selectedId);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []); // Empty dependency array - handler is now stable

  return (
    <TooltipProvider>
      {/* Live region for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>

      <div className="flex items-center gap-1 p-2 border rounded-lg bg-card">
        {/* Undo/Redo Group */}
        <div className="flex items-center gap-1 px-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  undo();
                  announce(t('production.editorToolbar.announced.undo'));
                }}
                disabled={!canUndo}
              >
                <Undo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('production.editorToolbar.undo')}</p>
              {canUndo && (
                <p className="text-xs text-muted-foreground">{t('production.editorToolbar.steps', { count: pastStates.length })}</p>
              )}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  redo();
                  announce(t('production.editorToolbar.announced.redo'));
                }}
                disabled={!canRedo}
              >
                <Redo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('production.editorToolbar.redo')}</p>
              {canRedo && (
                <p className="text-xs text-muted-foreground">{t('production.editorToolbar.steps', { count: futureStates.length })}</p>
              )}
            </TooltipContent>
          </Tooltip>

          {/* History depth indicator */}
          {(pastStates.length > 0 || futureStates.length > 0) && (
            <span className="text-xs text-muted-foreground flex items-center gap-1 ml-1">
              <History className="h-3 w-3" />
              {pastStates.length}/{pastStates.length + futureStates.length}
            </span>
          )}
        </div>

        <Separator orientation="vertical" className="h-6 mx-2" />

        {/* Zoom Controls Group */}
        <div className="flex items-center gap-1 px-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  const newScale = Math.max(1, scale - 0.5);
                  setScale(newScale);
                  announce(t('production.editorToolbar.announced.zoom', { percent: Math.round(newScale * 100) }));
                }}
                disabled={scale <= 1}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('production.editorToolbar.zoomOut')}</TooltipContent>
          </Tooltip>

          <span className="text-sm w-12 text-center font-mono">{Math.round(scale * 100)}%</span>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  const newScale = Math.min(5, scale + 0.5);
                  setScale(newScale);
                  announce(t('production.editorToolbar.announced.zoom', { percent: Math.round(newScale * 100) }));
                }}
                disabled={scale >= 5}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('production.editorToolbar.zoomIn')}</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6 mx-2" />

        {/* Grid Controls Group */}
        <div className="flex items-center gap-1 px-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Toggle
                pressed={showGrid}
                onPressedChange={toggleGrid}
                aria-label={t('production.editorToolbar.toggleGrid')}
              >
                <Grid3X3 className="h-4 w-4" />
              </Toggle>
            </TooltipTrigger>
            <TooltipContent>{t('production.editorToolbar.showGrid')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Toggle
                pressed={snapToGrid}
                onPressedChange={toggleSnap}
                aria-label={t('production.editorToolbar.toggleSnap')}
                className="text-xs px-2"
              >
                {t('production.editorToolbar.snapToGrid')}
              </Toggle>
            </TooltipTrigger>
            <TooltipContent>{t('production.editorToolbar.snapToGrid')}</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6 mx-2" />

        {/* Reset */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setResetDialogOpen(true)}
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {t('production.editorToolbar.reset')}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('production.editorToolbar.resetToDefault')}</TooltipContent>
        </Tooltip>

        <div className="flex-1" />

        {/* Keyboard Shortcuts Help */}
        <KeyboardShortcutsHelp />
      </div>

      {/* Reset confirmation dialog */}
      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('production.editorToolbar.resetConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('production.editorToolbar.resetConfirmDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('production.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { reset(); setResetDialogOpen(false); }}>
              {t('production.editorToolbar.resetConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
